import React, { useRef, useState, useMemo, useEffect } from 'react'
import { ApiDTO_NTSPform, ApiDTO_NTSPUI_ElementList, NTSPElement } from './Ntsp_Slice'
import moment from 'moment'
import DatePicker from '../../GenericComponents/DatePicker'
import { FoundationsButton } from '../../GenericComponents/button'
import { CopyIcon } from '../../GenericComponents/CopyIcon'
import { isNumber } from 'underscore'
import { getNowInFacilityTimezone, validateForm } from './NTSP_StatPortal'  // F-14213: Importing utility functions for time zone handling
import { tzCodeToIANA, formatDateTimeLocal } from '../../Utils'  // FP-14213: Importing timezone code mapping utility

declare let window: any
const UnicornApp = window.unicornInstance as any    // FP-14213: Access UnicornApp to check user privileges

// Props interface for PostMRSTracking component
interface PostMRSTrackingProps {
    NTSPElements?: ApiDTO_NTSPUI_ElementList
    form: ApiDTO_NTSPform
    errors?: { [s: string]: string }
    setErrors?: (prev: any) => void
    validationSchema?: { [key: string]: (value: any, ntspElements?: ApiDTO_NTSPUI_ElementList) => string }
    setForm: React.Dispatch<React.SetStateAction<ApiDTO_NTSPform>>
    handleTabSwitch: (tab: any) => void
    handleSave: () => void
    handleClear: () => void   // FP-14213: Adding handleClear to props for Post MRS Tracking tab
    isSaved: boolean
}

// Represents a single option/answer choice for an assessment question
interface AssessmentQuestionOption {
    ansId: string,
    answer: string,
    value: boolean | null,
    nextQueId: string | null,
    score: number | null
}

// Represents a single Modified Rankin Scale assessment question
interface AssessmentQuestion {
    queId: string,
    question: any,
    visible: boolean,
    options: AssessmentQuestionOption[]
}

const questionBank: AssessmentQuestion[] = [
    {
        queId: 'flag_Q1',
        question: <>Could you live alone without any help from another person? <br /> (This means being able to bathe, use the toilet, shop, prepare or get meals, and manage finances.)</>,
        visible: true, // Q1 is always the first question shown
        options: [
            {
                ansId: 'Q1_yes',
                answer: 'Yes',
                value: true,
                nextQueId: 'flag_Q2',
                score: null
            },
            {
                ansId: 'Q1_no',
                answer: 'No',
                value: false,
                nextQueId: 'flag_Q3',
                score: null
            }
        ]
    },
    {
        queId: 'flag_Q2',
        question: <>Can you do everything that you were doing right before your stroke, <br /> even if slower and not as much?</>,
        visible: false,
        options: [
            {
                ansId: 'Q2_yes',
                answer: 'Yes',
                value: true,
                nextQueId: 'flag_Q4',
                score: null
            },
            {
                ansId: 'Q2_no',
                answer: 'No',
                value: false,
                nextQueId: null,
                score: 2
            }
        ]
    },
    {
        queId: 'flag_Q3',
        question: <>Can you walk from one room to another without help from another person?</>,
        visible: false,
        options: [
            {
                ansId: 'Q3_yes',
                answer: 'Yes',
                value: true,
                nextQueId: null,
                score: 3
            },
            {
                ansId: 'Q3_no',
                answer: 'No',
                value: false,
                nextQueId: 'flag_Q5',
                score: null
            }
        ]
    },
    {
        queId: 'flag_Q4',
        question: <>Are you completely back to the way you were right before your stroke?</>,
        visible: false,
        options: [
            {
                ansId: 'Q4_yes',
                answer: 'Yes',
                value: true,
                nextQueId: null,
                score: 0
            },
            {
                ansId: 'Q4_no',
                answer: 'No',
                value: false,
                nextQueId: null,
                score: 1
            }
        ]
    },
    {
        queId: 'flag_Q5',
        question: <>Can you sit up in bed without any help?</>,
        visible: false,
        options: [
            {
                ansId: 'Q5_yes',
                answer: 'Yes',
                value: true,
                nextQueId: null,
                score: 4
            },
            {
                ansId: 'Q5_no',
                answer: 'No',
                value: false,
                nextQueId: null,
                score: 5
            }
        ]
    },
];

// Maps MRS scores (0-6) to their clinical descriptions
// These labels provide context for the numeric score values
const scoreLabels: { [key: number]: string } = {
    0: 'No symptoms at all.',
    1: 'No significant disability despite symptoms.',
    2: 'Slight disability.',
    3: 'Moderate disability.',
    4: 'Moderately severe disability.',
    5: 'Severe disability.',
    6: 'Death',
};

// FP-14213: adding isSaved, handleClear to props
const PostMRSTracking: React.FC<PostMRSTrackingProps> = ({ NTSPElements, form, errors, setErrors, validationSchema, setForm, handleTabSwitch, handleSave, isSaved, handleClear }) => {

    if (errors === undefined) errors = {}
    if (setErrors === undefined) setErrors = (errors: any) => { }
    if (validationSchema === undefined) validationSchema = {}
    const [formError, setFormError] = useState<string>('')

    const [toggleAssessment, setToggleAssessment] = useState(true);
    const [toggleSummary, setToggleSummary] = useState(true);
    const [isNightMode, setIsNightMode] = useState(document.body.classList.contains('night-mode'));
    const [assessmentQuestions, setAssessmentQuestions] = useState<AssessmentQuestion[]>(questionBank);
    const [formStatus, setFormStatus] = useState<number>(form.fk_MRSFormStatus || -1);
    const [currentAttemptState, setCurrentAttemptState] = useState<boolean>(form.fk_MRSFormStatus === 2);
    const [currentAttempt, setCurrentAttempt] = useState<number>(-1);
    const [attemptsTS, setAttemptsTS] = useState<{ [key: number]: string | null }>({ 1: form.ts_Attempt1 || null, 2: form.ts_Attempt2 || null, 3: form.ts_Attempt3 || null });
    const [TotalScore, setTotalScore] = useState<number | null>(isNumber(form.TotalScore) ? form.TotalScore : null);
    const [submitted, setSubmitted] = useState<boolean>(false)  // FP-14213: Track whether the form has been submitted
    const [saving, setSaving] = useState<boolean>(false)    // FP-14213: Track whether the form is in the process of being saved
    const [isCustomerSite] = useState<boolean>(UnicornApp.checkUserPrivileges("NTSPCustomer"));
    const [dischargeDate, setDischargeDate] = useState<any>(form.ts_Discharge); // FP-14213: local state for discharge date

    // FP-14213: Ref to store the initial form state for comparison and resetting purposes
    const formStateRef = useRef({
        fk_MRSFormStatus: form.fk_MRSFormStatus,
        flag_CallSuccessful: form.flag_CallSuccessful,
        CurrentAttempt: form.CurrentAttempt,
        ts_Attempt1: form.ts_Attempt1,
        ts_Attempt2: form.ts_Attempt2,
        ts_Attempt3: form.ts_Attempt3,
        flag_IsPatientDeceased: form.flag_IsPatientDeceased,
        flag_Q1: form.flag_Q1,
        flag_Q2: form.flag_Q2,
        flag_Q3: form.flag_Q3,
        flag_Q4: form.flag_Q4,
        flag_Q5: form.flag_Q5,
        TotalScore: form.TotalScore,
        ts_Discharge: form.ts_Discharge,
        MRSNote: form.MRSNote,
    });

    // Listen for theme changes
    React.useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsNightMode(document.body.classList.contains('night-mode'));
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // Initialize component on mount
    // Determines the current attempt number and initializes assessment visibility based on saved answers
    useEffect(() => {
        // If previous call was successful, resume that attempt; otherwise move to next attempt
        if (form.flag_CallSuccessful === true || form.fk_MRSFormStatus === 2) {
            setCurrentAttempt(form.CurrentAttempt || -1);
        } else {
            if ((!form.CurrentAttempt || form.CurrentAttempt == -1))
                setCurrentAttempt(1); // Start with attempt 1 if none exists
            else
                setCurrentAttempt(form.CurrentAttempt + 1); // Move to next attempt
        }

        // Ensure flag_CallSuccessful has a boolean value (default to false)
        setForm((prev) => ({
            ...prev,
            flag_CallSuccessful: form.flag_CallSuccessful === true || form.flag_CallSuccessful === false ? form.flag_CallSuccessful : false,
        }));

        // Show questions that have already been answered (restore form state from saved data)
        setAssessmentQuestions((prev) => {
            return prev.map((q) => {
                if ((form as any)[q.queId] !== null) {
                    return { ...q, visible: true };
                }
                return q;
            });
        });

        return () => {
            // FP-14213: Cleanup function to reset form state when component unmounts
            setForm((prev) => ({
                ...prev,
                fk_MRSFormStatus: formStateRef.current.fk_MRSFormStatus,
                flag_CallSuccessful: formStateRef.current.flag_CallSuccessful,
                CurrentAttempt: formStateRef.current.CurrentAttempt,
                ts_Attempt1: formStateRef.current.ts_Attempt1,
                ts_Attempt2: formStateRef.current.ts_Attempt2,
                ts_Attempt3: formStateRef.current.ts_Attempt3,
                flag_IsPatientDeceased: formStateRef.current.flag_IsPatientDeceased,
                flag_Q1: formStateRef.current.flag_Q1,
                flag_Q2: formStateRef.current.flag_Q2,
                flag_Q3: formStateRef.current.flag_Q3,
                flag_Q4: formStateRef.current.flag_Q4,
                flag_Q5: formStateRef.current.flag_Q5,
                TotalScore: formStateRef.current.TotalScore,
                ts_Discharge: formStateRef.current.ts_Discharge,
                MRSNote: formStateRef.current.MRSNote,
            }));
            // FP-14213: Clear MRS form specific errors on unmount
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                ts_Discharge: '',
                CurrentAttempt: '',
                flag_IsPatientDeceased: '',
                flag_Q1: '',
                flag_Q2: '',
                flag_Q3: '',
                flag_Q4: '',
                flag_Q5: '',
            }));
            setFormError('');
        }
    }, [])

    // Sync the current attempt number to the form state whenever it changes
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            CurrentAttempt: currentAttempt,
        }));
    }, [currentAttempt, form.CurrentAttempt]);

    // Record the timestamp when an attempt is marked as started
    // Only saves timestamp when the attempt checkbox is checked (currentAttemptState = true)
    useEffect(() => {
        const tzNow = getNowInFacilityTimezone(form.fk_Facility, NTSPElements, tzCodeToIANA);   // FP-14213: Get current time in facility timezone
        const now = formatDateTimeLocal(new Date(tzNow)); // FP-14213: Use utility function to get current time in facility timezone
        if (currentAttempt === 1)
            setForm((prev) => ({
                ...prev,
                ts_Attempt1: currentAttemptState === true ? now : null, // Save timestamp or clear it
            }));
        if (currentAttempt === 2)
            setForm((prev) => ({
                ...prev,
                ts_Attempt2: currentAttemptState === true ? now : null,
            }));
        if (currentAttempt === 3)
            setForm((prev) => ({
                ...prev,
                ts_Attempt3: currentAttemptState === true ? now : null,
            }));
    }, [currentAttemptState, currentAttempt, form.ts_Attempt1, form.ts_Attempt2, form.ts_Attempt3]);

    // Clear assessment data when the call was unsuccessful
    // This resets questions and scores since we couldn't reach the patient
    useEffect(() => {
        if (form.flag_CallSuccessful === false) {
            setForm((prev) => ({
                ...prev,
                flag_IsPatientDeceased: null, // Clear deceased flag
                flag_Q1: null, // Clear all assessment answers
                flag_Q2: null,
                flag_Q3: null,
                flag_Q4: null,
                flag_Q5: null,
                TotalScore: null, // Clear score
            }));
        }
    }, [form.flag_CallSuccessful]);

    // Auto-expand the assessment section when conditions are met for starting the assessment
    // Assessment can only be conducted if call was successful and patient is alive
    useEffect(() => {
        if (form.flag_CallSuccessful === true && form.flag_IsPatientDeceased === false) {
            setToggleAssessment(true); // Automatically expand assessment section
        }
    }, [form.flag_CallSuccessful, form.flag_IsPatientDeceased]);

    // Update form status based on attempt progress and call success
    // Status 2 = Completed (call successful or all 3 attempts made), Status 1 = Pending
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            fk_MRSFormStatus: form.flag_CallSuccessful === true || currentAttempt === 3 ? 2 : 1,
        }));
    }, [currentAttempt, form.flag_CallSuccessful, form.CurrentAttempt]);

    const unsuccessfulAttempts = useMemo(() => {
        return form.CurrentAttempt === 3 && currentAttemptState === true && form.flag_CallSuccessful === false;
    }, [form.CurrentAttempt, currentAttemptState, form.flag_CallSuccessful]);

    // Memoized lookup to get the friendly display name of the current form status
    // Prevents unnecessary recalculations and lookups
    const getFormStatus = useMemo(() => {
        return NTSPElements?.MRSStatus?.find(e => e._key === formStatus)?.FriendlyName;
    }, [formStatus]);

    // Updates form when user indicates whether the call attempt was successful
    const handleCallAttemptSuccessful = (isSuccessful: boolean) => {
        setForm((prev) => ({
            ...prev,
            flag_CallSuccessful: isSuccessful,
        }));

        if (!isSuccessful) {
            setErrors((prev: { [s: string]: string }) => ({
                ...prev,
                ['flag_IsPatientDeceased']: '',
            }));
        }
    };

    // Handles the deceased status change
    // If patient is deceased, automatically assigns score of 6 (per MRS scale)
    const handleDeceasedChange = (isDeceased: boolean) => {
        setForm((prev) => ({
            ...prev,
            flag_IsPatientDeceased: isDeceased,
            TotalScore: isDeceased ? 6 : null,  // If deceased, set score to 6 automatically
        }));

        if (!isDeceased) {
            // Reset assessment if patient is marked as alive
            resetQuestion('flag_Q1');

            setAssessmentQuestions((prev) => {
                return prev.map((q) => {
                    if (q.queId === 'flag_Q1') {
                        return { ...q, visible: true };
                    }
                    return q;
                });
            });
        }

        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            ['flag_IsPatientDeceased']: '',
        }));
    };

    // Recursively clears an answer and all subsequent answers that depend on it
    // Used to reset downstream questions when a previous answer is changed
    // This ensures the assessment maintains logical consistency
    const resetQuestion = (questionId: string | null) => {
        if (!questionId)
            return;

        // Clear the answer from form state
        setForm((prev) => ({
            ...prev,
            [questionId]: null,
        }));

        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            [questionId]: '',
        }));

        // Hide the question from view
        setAssessmentQuestions((prev) => {
            return prev.map((q) => {
                if (q.queId === questionId) {
                    return { ...q, visible: false };
                }
                return q;
            });
        });

        // Recursively clear all questions that branch from this one
        const que = assessmentQuestions.find(e => e.queId == questionId);
        if (que) {
            for (let opt of que?.options)
                resetQuestion(opt.nextQueId); // Clear dependent questions
        }
    };

    // Handles user selecting an answer option for an assessment question
    // Updates the answer, shows the next question, and updates the score if terminal answer
    const handleAssessmentAnswer = (questionId: string, option: AssessmentQuestionOption) => {
        // Clear this question and all dependent questions
        resetQuestion(questionId);

        // Record the selected answer
        setForm((prev) => ({
            ...prev,
            [questionId]: option.value,
        }));

        // Make current and next questions visible (enables question branching)
        setAssessmentQuestions((prev) => {
            return prev.map((q) => {
                if (q.queId === questionId || q.queId === option.nextQueId) {
                    return { ...q, visible: true };
                }
                return q;
            });
        });

        // Update total score if this answer has a defined score (terminal node)
        setForm((prev) => ({
            ...prev,
            TotalScore: option.score,
        }));
    };

    // Handles form submission - saves data and updates UI state
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const tempErrors = validateForm(form, validationSchema)
        // Merge existing errors (including date validation errors) with new validation errors
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)


        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            return
        } else {
            setErrors({})
            setFormError('')
            // Call parent component's save handler if provided
            if (handleSave) await handleSave()

            // FP-14213: If save was unsuccessful, don't update form state
            if (!isSaved) return;

            // FP-14213: Update the formStateRef to the latest form state after saving, so that if the user navigates away and back, 
            // it reflects the most recently saved state instead of the initial state when the component first mounted.
            formStateRef.current = {
                fk_MRSFormStatus: form.fk_MRSFormStatus,
                flag_CallSuccessful: form.flag_CallSuccessful,
                CurrentAttempt: form.CurrentAttempt,
                ts_Attempt1: form.ts_Attempt1,
                ts_Attempt2: form.ts_Attempt2,
                ts_Attempt3: form.ts_Attempt3,
                flag_IsPatientDeceased: form.flag_IsPatientDeceased,
                flag_Q1: form.flag_Q1,
                flag_Q2: form.flag_Q2,
                flag_Q3: form.flag_Q3,
                flag_Q4: form.flag_Q4,
                flag_Q5: form.flag_Q5,
                TotalScore: form.TotalScore,
                ts_Discharge: form.ts_Discharge,
                MRSNote: form.MRSNote,
            }

            setToggleSummary(true); // Auto-expand summary section after submit

            // Increment attempt count if unsuccessful; keep same attempt if successful
            setCurrentAttempt((prev) => form.flag_CallSuccessful === true || form.fk_MRSFormStatus === 2 ? prev : prev + 1);
            setFormStatus(form.fk_MRSFormStatus || -1);
            setCurrentAttemptState(form.fk_MRSFormStatus === 2);
            // Sync attempt timestamps from form state
            setAttemptsTS({ 1: form.ts_Attempt1 || null, 2: form.ts_Attempt2 || null, 3: form.ts_Attempt3 || null });
            setTotalScore(isNumber(form.TotalScore) ? form.TotalScore : null);
            setDischargeDate(form.ts_Discharge);    // FP-14213: Sync discharge date to local state
            return
        }
    };

    // FP-14213: handle close button click
    const wrapHandleClear = async () => {
        setSubmitted(true)
        setSaving(true)
        const tempErrors = validateForm(form, validationSchema)
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            setSaving(false)
            return
        } else {
            setErrors({})
            setFormError('')
            if (handleSave) await handleSave()
            if (handleClear) handleClear()
            setSaving(false)
            return
        }
    }
    //FP-14558 append to successful attempt note when call is successful and patient identity is confirmed
    const successfulAttemptNoteSuffix = ' Veteran confirmed identity via two identifiers. Patient consented to phone call for modified Rankin Evaluation.';

    // Generates a human-readable description of a call attempt for the summary display
    // Shows different messages depending on whether the attempt was successful
    const getAttemptDescription = (attemptNum: number): string | React.ReactNode => {
        let timestamp: string | null | undefined = null;
        let isSuccessful: boolean | undefined = false;
        // Determine if this specific attempt was the successful one
        isSuccessful = form.flag_CallSuccessful === true && form.CurrentAttempt === attemptNum;

        if (attemptNum === 1 && attemptsTS[1]) {
            timestamp = moment(attemptsTS[1])?.format('MM/DD/YYYY HH:mm') || '';
        } else if (attemptNum === 2 && attemptsTS[2]) {
            timestamp = moment(attemptsTS[2])?.format('MM/DD/YYYY HH:mm') || '';
        } else if (attemptNum === 3 && attemptsTS[3]) {
            timestamp = moment(attemptsTS[3])?.format('MM/DD/YYYY HH:mm') || '';
        }

        if (!timestamp) return '';

        const attemptLabel = NTSPElements?.CallAttempts.find(ca => ca._key === attemptNum)?.FriendlyName;

        // If successful, show success message
        if (isSuccessful) {
            return (
                <>
                    <label className='mr-1' style={{ minWidth: 'max-content' }}>{attemptLabel}:</label>
                    {` ${timestamp} Call attempt was successful.${successfulAttemptNoteSuffix}`} {/* FP-14558 append to successful attempt note when call is successful and patient identity is confirmed */}
                </>
            );
        }

        // If unsuccessful, show the standard unsuccessful message
        const unsuccessfulText = ` ${timestamp} Attempted to reach veteran at this time to conduct a post-event modified Rankin Scale evaluation, no answer. HIPAA compliant voicemail left.`;
        const willAttemptText = attemptNum < 3 ? ' Will attempt to reach again at a later date/time.' : '';

        return (
            <>
                <label className='mr-1' style={{ minWidth: 'max-content' }}>{attemptLabel}:</label>
                {unsuccessfulText + willAttemptText}
            </>
        );
    };

    // Generates a plain text version of the attempt description for copying to clipboard
    // Used when user clicks "Copy to CPRS" button
    const getAttemptDescriptionText = (attemptNum: number): string => {
        let timestamp: string | null | undefined = null;
        let isSuccessful: boolean | undefined = false;
        isSuccessful = form.flag_CallSuccessful === true && form.CurrentAttempt === attemptNum;

        if (attemptNum === 1 && attemptsTS[1]) {
            timestamp = moment(attemptsTS[1])?.format('MM/DD/YYYY HH:mm') || '';
        } else if (attemptNum === 2 && attemptsTS[2]) {
            timestamp = moment(attemptsTS[2])?.format('MM/DD/YYYY HH:mm') || '';
        } else if (attemptNum === 3 && attemptsTS[3]) {
            timestamp = moment(attemptsTS[3])?.format('MM/DD/YYYY HH:mm') || '';
        }

        if (!timestamp) return '';

        const attemptLabel = NTSPElements?.CallAttempts.find(ca => ca._key === attemptNum)?.FriendlyName;

        // If successful, show success message
        if (isSuccessful) {
            return `${attemptLabel}: ${timestamp} Call attempt was successful.${successfulAttemptNoteSuffix}`; //FP-14558 append to successful attempt note when call is successful and patient identity is confirmed
        }

        // If unsuccessful, show the standard unsuccessful message
        const unsuccessfulText = `${attemptLabel}: ${timestamp} Attempted to reach veteran at this time to conduct a post-event modified Rankin Scale evaluation, no answer. HIPAA compliant voicemail left.`;
        const willAttemptText = attemptNum < 3 ? ' Will attempt to reach again at a later date/time.' : '';

        return unsuccessfulText + willAttemptText;
    };

    useEffect(() => {
        if (!Object.values(errors) || Object.values(errors).join('').length === 0) {
            setFormError('')
        }
    }, [errors])

    // Handler for date field validation errors from DatePicker
    const handleDateValidationError = (fieldName: string) => (error: string) => {
        setErrors((prev: { [s: string]: string }) => ({
            ...prev,
            [fieldName]: error,
        }))
    }

    return (<>
        <form
            onSubmit={handleSubmit}
            className="ntsp-form-container"
        >
            <div className="row">
                <div className='col-sm-8'>
                    <div style={{ display: 'flex', gap: '4rem' }}>
                        <div>
                            <label className='mr-1' style={{ minWidth: 'max-content' }}>Patient Name:</label>{' '}
                            {`${form.PatientFirstName || 'Unknown'} ${form.PatientLastName || 'Unknown'}`}
                        </div>
                        <div>
                            <label className='mr-1' style={{ minWidth: 'max-content' }}>Initial Consult Date:</label>{' '}
                            {moment(form.ts_Consult)?.format('MM/DD/YYYY') || 'Unknown'}
                        </div>

                        <div>
                            {/* FP-14213: prevent overlapping */}
                            <div className="form-group" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
                                <label style={{ minWidth: 'max-content' }}>
                                    Date of Discharge*:
                                </label>
                                <span>
                                    <DatePicker
                                        id="ts_Discharge"
                                        name="ts_Discharge"
                                        dateFormat="MM/dd/yyyy"
                                        showMasking={true}
                                        inputClass="ntsp-fields"
                                        showTime={false}
                                        startingDate={form.ts_Discharge && form.fk_Facility !== -1 ? new Date(form.ts_Discharge) : null}
                                        setStartingDate={(dateString) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                ts_Discharge: dateString,
                                            }));
                                        }}
                                        placeholder="Select Date"   // FP-14213: Updated placeholder text
                                        style={{ width: '60%' }}
                                        isClearable={true}
                                        classes={errors.ts_Discharge ? 'error-field' : ''}
                                        onValidationError={handleDateValidationError('ts_Discharge')}
                                    />
                                    {errors.ts_Discharge && (
                                        <div
                                            style={{
                                                color: 'red',
                                                //fontSize: '12px', // FP-14213:
                                                marginTop: '4px',
                                                maxWidth: 'max-content',
                                            }}
                                        >
                                            {errors.ts_Discharge + ''}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Call attempt tracking section - allows user to record up to 3 call attempts */}
                    <div className='form-row mt-4'>
                        <div className='col-sm'>
                            <label className='mr-3' style={{ minWidth: 'max-content' }}>Call Attempts:</label>{' '}
                            {/* Render checkboxes for each call attempt (typically 3) */}
                            {NTSPElements?.CallAttempts?.map(
                                (pos: NTSPElement, index: number) => {
                                    return (
                                        <>
                                            <span className='mr-4' key={pos._key}>
                                                <input
                                                    type="checkbox"
                                                    id={`flag_attempt${pos._key}`}
                                                    name={`flag_attempt${pos._key}`}
                                                    value={pos._key}
                                                    className="ntsp-fields"
                                                    checked={pos._key < currentAttempt || (currentAttemptState && pos._key === currentAttempt)}
                                                    onChange={(e) => {
                                                        setCurrentAttemptState(e.target.checked)
                                                        setErrors((prev: { [s: string]: string }) => ({
                                                            ...prev,
                                                            ['CurrentAttempt']: '',
                                                        }));
                                                    }}
                                                    disabled={pos._key != currentAttempt || formStatus === 2}
                                                />
                                                <label htmlFor={`flag_attempt${pos._key}`}
                                                    style={{
                                                        minWidth: 'max-content', marginLeft: '0.5rem',
                                                        cursor: (pos._key == currentAttempt && formStatus !== 2) ? 'pointer' : 'not-allowed',   // FP-14213:
                                                        color: (pos._key == currentAttempt && formStatus !== 2) ? 'inherit' : '#999'
                                                    }}>
                                                    {pos.FriendlyName}
                                                </label>
                                            </span>
                                        </>
                                    )
                                }
                            )}
                            {
                                errors[`CurrentAttempt`] && (
                                    <div
                                        style={{
                                            color: 'red',
                                            //fontSize: '12px', // FP-14213:
                                            marginTop: '4px',
                                            maxWidth: 'max-content',
                                        }}
                                    >
                                        {errors.CurrentAttempt + ''}
                                    </div>
                                )
                            }
                        </div>
                        {/* Toggle to indicate if the call attempt was successful */}
                        <div className='col-sm'>
                            <div className="form-group">
                                <label className="ml-4">
                                    Call attempt successful
                                </label>
                                <span>
                                    <input
                                        type="checkbox"
                                        id="flag_CallSuccessful"
                                        name="flag_CallSuccessful"
                                        className="generic-switch"
                                        checked={form.flag_CallSuccessful === true}
                                        onChange={(e) => handleCallAttemptSuccessful(e.target.checked)}
                                    />
                                    <i className="helper"></i>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Only show "Is the patient deceased?" if call attempt was successful */}
                    {form.flag_CallSuccessful === true && (
                        <div className='form-row mt-4'>
                            <div className='col-sm-12'>
                                <label className='mr-3'>Is the patient deceased?</label>
                                <span>
                                    <input
                                        type="radio"
                                        name="flag_IsPatientDeceased"
                                        id={`patientDeceased_yes`}
                                        checked={form.flag_IsPatientDeceased === true}
                                        onChange={() => handleDeceasedChange(true)}
                                    />
                                    <label
                                        className="ml-2"
                                        style={{ minWidth: '65px' }}
                                        htmlFor={`patientDeceased_yes`}
                                    >
                                        Yes
                                    </label>
                                </span>
                                <span>
                                    <input
                                        type="radio"
                                        name="flag_IsPatientDeceased"
                                        id={`patientDeceased_no`}
                                        checked={form.flag_IsPatientDeceased === false}
                                        onChange={() => handleDeceasedChange(false)}
                                    />
                                    <label
                                        className="ml-2"
                                        style={{ minWidth: '65px' }}
                                        htmlFor={`patientDeceased_no`}
                                    >
                                        No
                                    </label>
                                </span>
                                {
                                    errors.flag_IsPatientDeceased && (
                                        <div
                                            style={{
                                                color: 'red',
                                                //fontSize: '12px', // FP-14213:
                                                marginTop: '4px',
                                                maxWidth: 'max-content',
                                            }}
                                        >
                                            {errors.flag_IsPatientDeceased + ''}
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    )}
                </div>
                <div className='col-sm-4'>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.3rem',
                            width: '50%',
                            minWidth: 'fit-content',
                            position: 'relative',
                        }}
                    >
                        <label>Notes: </label>
                        <textarea
                            id="MRSNotes"
                            name="MRSNotes"
                            className="ntsp-fields"
                            style={{ height: '6rem', borderRadius: '4px' }}
                            placeholder="Add notes here"
                            value={form.MRSNote}
                            maxLength={2000}
                            onChange={(e) => {
                                setForm((prev) => ({
                                    ...prev,
                                    MRSNote: e.target.value,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Assessment section - only shown if call was successful and patient is alive */}
            {/* The assessment contains branching MRS questions to determine the patient's functional status */}
            {form.flag_CallSuccessful === true && form.flag_IsPatientDeceased === false && (
                <>
                    <hr className="mb-1" />
                    {/* Collapsible section header for assessment */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',   // FP-14213:
                            justifyContent: 'space-between',
                            fontWeight: 500,
                            fontSize: 16,
                            marginLeft: '-5px',
                            padding: '5px',
                            marginBottom: '1.5rem',
                        }}
                    >
                        {/* FP-14213: pointer only for toggle */}
                        <div
                            style={{
                                display: 'flex',    // FP-14213:
                                alignItems: 'flex-start',
                                cursor: 'pointer',
                            }}
                            onClick={() => setToggleAssessment((v) => !v)}  // FP-14213:
                        >
                            {/* FP-14213: Animated arrow indicating expand/collapse state */}
                            <span
                                style={{
                                    fontSize: 14,
                                    marginRight: 8,
                                    userSelect: 'none',
                                    transition: 'transform 0.2s',
                                    transform: toggleAssessment ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            >
                                ▶
                            </span>
                            Start Assessment
                        </div>
                    </div>

                    {/* Render assessment questions that are marked as visible */}
                    {toggleAssessment && (() => {
                        return assessmentQuestions.filter(e => e.visible).map((que, i) => {
                            return <div className='form-row mt-4' key={que.queId}>
                                <div className='col-sm'>
                                    <label>{i + 1}. {que.question}</label>
                                </div>
                                <div className='col-sm'>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', lineHeight: '1' }}>
                                        {
                                            que.options.map((opt) => {
                                                return <div>
                                                    <input
                                                        type="radio"
                                                        name={que.queId}
                                                        id={opt.ansId}
                                                        checked={(form as any)[que.queId] == opt.value}
                                                        onChange={() => handleAssessmentAnswer(que.queId, opt)}
                                                    />
                                                    <label
                                                        className="ml-2"
                                                        style={{ minWidth: '65px' }}
                                                        htmlFor={opt.ansId}
                                                    >
                                                        {opt.answer}
                                                    </label>
                                                </div>
                                            })
                                        }
                                    </div>
                                    {
                                        errors[que.queId] && (
                                            <div
                                                style={{
                                                    color: 'red',
                                                    //fontSize: '12px', // FP-14213:
                                                    marginTop: '4px',
                                                    maxWidth: 'max-content',
                                                }}
                                            >
                                                {errors[que.queId] + ''}
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        });
                    })()}
                </>
            )}

            {/* Score Display Section - Shows the final MRS score with color-coded severity indicator */}
            {/* Score is displayed when assessment is complete or patient is marked as deceased */}
            {((form.TotalScore !== null) || unsuccessfulAttempts) && (
                <div
                    style={{
                        marginTop: '2rem',
                        marginBottom: '2rem',
                    }}
                >
                    {(() => {
                        let bgColor = '#A6EC9838'; // Green for 0-1
                        let textColor = '#10AD2D'; // Dark green for 0-1

                        if (unsuccessfulAttempts) {
                            bgColor = '#49505775'; // Grey for all unsuccessful attempts
                            textColor = '#808080'; // Dark grey for all unsuccessful attempts                          
                        } else if (form.TotalScore && form.TotalScore >= 2 && form.TotalScore <= 4) {
                            bgColor = '#FECD811F'; // Yellow/Orange for 2-4
                            textColor = '#FA8E02'; // Dark yellow/orange for 2-4
                        } else if (form.TotalScore && form.TotalScore >= 5) {
                            bgColor = '#F5D3D31F'; // Red for 5-6
                            textColor = '#BD4244'; // Dark red for 5-6
                        }

                        let scoreToShow = '';
                        if (unsuccessfulAttempts)
                            scoreToShow = 'All 3 call attempts completed.';
                        else
                            scoreToShow = `${form.flag_IsPatientDeceased ? 'Automatic ' : ''}Score: ${form.TotalScore} - ${scoreLabels[form.TotalScore || 0]}`

                        return (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: bgColor,
                                    borderRadius: '4px',
                                    border: `1.6px solid ${textColor}`,
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    display: 'inline-block',
                                    minWidth: '354px',
                                }}
                            >
                                {scoreToShow}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Buttons */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '2rem',
                }}
            >
                <FoundationsButton
                    type="submit"
                    buttonClasses="app-button"
                    id={''}
                    disabled={saving || (isCustomerSite && form.fk_Outcome !== -1)}   // FP-14213: Disable Save button while saving
                >
                    Save
                </FoundationsButton>
                <FoundationsButton
                    type="button"
                    buttonClasses="app-button"
                    id={''}
                    disabled={saving || (isCustomerSite && form.fk_Outcome !== -1)}   // FP-14213: Disable Save button while saving
                    onClick={wrapHandleClear}   // FP-14213: Clear form and reset state
                    toolTipText="Currently opened case entry form will be saved and closed."    // FP-14213: Add tooltip
                    tooltipClasses="info-icon"
                >
                    Close
                </FoundationsButton>
            </div>
            {/* submitted */}
            {formError !== '' && (
                <div style={{ color: 'red', textAlign: 'right', marginTop: '1rem' }}>
                    {formError}
                </div>
            )}

            {/* Summary Section - Shows form status and collapsible summary of all interactions */}
            {/* Only displayed after form has been submitted or saved (formStatus !== -1) */}
            {formStatus !== -1 && (
                <>
                    <hr className="mb-1" />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontWeight: 500,
                            fontSize: 16,
                            marginLeft: '-5px',
                            padding: '5px',
                            marginBottom: '1.5rem',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            onClick={() => setToggleSummary((v) => !v)}
                        >
                            <span
                                style={{
                                    fontSize: 14,
                                    marginRight: 8,
                                    userSelect: 'none',
                                    transition: 'transform 0.2s',
                                    transform: toggleSummary ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            >
                                ▶
                            </span>
                            Summary
                        </div>
                        {/* Form status indicator - Green if completed, Yellow if in progress */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: 14, fontWeight: 500 }}>Form Status :</span>
                            <span style={{ color: formStatus === 2 ? '#28a745' : '#ffc107', fontWeight: 600 }}>{getFormStatus}</span>
                        </div>
                    </div>

                    {toggleSummary && (
                        <div style={{ padding: '1rem', borderRadius: '4px', border: '1px solid #767676', backgroundColor: isNightMode ? '#2E3235' : '#ffffff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                {/* FP-14213: rename Ranking to Rankin */}
                                <h5 style={{ margin: 0 }}>NTSP Post Event Modified Rankin Score</h5>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <label style={{ minWidth: 'max-content' }}>Copy to CPRS</label>
                                    {/* FP-14213: rename Ranking to Rankin */}
                                    <CopyIcon alwaysShow={true} copyText={`NTSP Post Event Modified Rankin Score\n\nPatient Name: ${form.PatientFirstName} ${form.PatientLastName}\nInitial Consult Date: ${moment(form.ts_Consult)?.format('MM/DD/YYYY') || 'N/A'}\nDate of Discharge: ${dischargeDate ? moment(dischargeDate)?.format('MM/DD/YYYY') : 'N/A'}\n\n${[1, 2, 3].map(i => getAttemptDescriptionText(i)).filter(desc => desc).join('\n\n')}${isNumber(TotalScore) ? `\n\nTotal Score: ${TotalScore} - ${scoreLabels[TotalScore || 0]}` : ''}`}
                                        id={'ntsp_mrs_summary'} customStyle={{
                                            padding: '0rem',
                                            backgroundColor: 'transparent',
                                            color: 'inherit',
                                            border: 'none',
                                            borderRadius: '0px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                        }} />
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                                <div>
                                    <label className='mr-1' style={{ minWidth: 'max-content' }}>Patient Name:</label> {form.PatientFirstName} {form.PatientLastName}
                                </div>
                                <div>
                                    <label className='mr-1' style={{ minWidth: 'max-content' }}>Initial Consult Date:</label> {moment(form.ts_Consult)?.format('MM/DD/YYYY') || 'N/A'}
                                </div>
                                <div>
                                    {/* FP-14213: show discharge date from local state */}
                                    <label className='mr-1' style={{ minWidth: 'max-content' }}>Date of Discharge:</label> {dischargeDate ? moment(dischargeDate)?.format('MM/DD/YYYY') : 'N/A'}
                                </div>
                            </div>

                            {/* Call attempt history - shows all attempt records from the interaction */}
                            {[1, 2, 3].map(attemptNum => {
                                const desc = getAttemptDescription(attemptNum);
                                return desc ? (
                                    <div style={{ marginBottom: '1rem' }}>
                                        {desc}
                                    </div>
                                ) : null;
                            })}

                            {/* Final MRS score and interpretation */}
                            {TotalScore != null && (
                                <div>
                                    <label className='mr-1' style={{ minWidth: 'max-content' }}>Total Score:</label> {TotalScore} - {scoreLabels[TotalScore || 0]}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </form>
    </>);
}

export default PostMRSTracking